import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type StringUpdater = string | null | ((prev: string | null) => string | null);
export type BooleanUpdater = boolean | ((prev: boolean) => boolean);

type ChatUIStore = {
    renameOpen: boolean;
    targetChatId: string | null;
    targetChatTitle: string | null;
    isSplashVisible: boolean;
    isConnecting: boolean;
    initialLoad: boolean;
    lastExportFilePath: string | null;
    lastExportDirectory: string | null;
    setRenameOpen: (value: BooleanUpdater) => void;
    setTargetChatId: (value: StringUpdater) => void;
    setTargetChatTitle: (value: StringUpdater) => void;
    setIsSplashVisible: (value: BooleanUpdater) => void;
    setIsConnecting: (value: BooleanUpdater) => void;
    setInitialLoad: (value: BooleanUpdater) => void;
    setLastExportFilePath: (value: StringUpdater) => void;
    setLastExportDirectory: (value: StringUpdater) => void;
};

const enableDevtools = process.env.NODE_ENV !== 'production';

export const useChatUIStore = create<ChatUIStore>()(
    devtools(
        (set) => ({
            renameOpen: false,
            targetChatId: null,
            targetChatTitle: null,
            isSplashVisible: true,
            isConnecting: false,
            initialLoad: false,
            lastExportFilePath: null,
            lastExportDirectory: null,

            setRenameOpen: (value) =>
                set(
                    (state) => ({
                        renameOpen:
                            typeof value === 'function'
                                ? value(state.renameOpen)
                                : value,
                    }),
                    false,
                    'chatUI/setRenameOpen',
                ),

            setTargetChatId: (value) =>
                set(
                    (state) => ({
                        targetChatId:
                            typeof value === 'function'
                                ? value(state.targetChatId)
                                : value,
                    }),
                    false,
                    'chatUI/setTargetChatId',
                ),

            setTargetChatTitle: (value) =>
                set(
                    (state) => ({
                        targetChatTitle:
                            typeof value === 'function'
                                ? value(state.targetChatTitle)
                                : value,
                    }),
                    false,
                    'chatUI/setTargetChatTitle',
                ),

            setIsSplashVisible: (value) =>
                set(
                    (state) => ({
                        isSplashVisible:
                            typeof value === 'function'
                                ? value(state.isSplashVisible)
                                : value,
                    }),
                    false,
                    'chatUI/setIsSplashVisible',
                ),

            setIsConnecting: (value) =>
                set(
                    (state) => ({
                        isConnecting:
                            typeof value === 'function'
                                ? value(state.isConnecting)
                                : value,
                    }),
                    false,
                    'chatUI/setIsConnecting',
                ),

            setInitialLoad: (value) =>
                set(
                    (state) => ({
                        initialLoad:
                            typeof value === 'function'
                                ? value(state.initialLoad)
                                : value,
                    }),
                    false,
                    'chatUI/setInitialLoad',
                ),

            setLastExportFilePath: (value) =>
                set(
                    (state) => ({
                        lastExportFilePath:
                            typeof value === 'function'
                                ? value(state.lastExportFilePath)
                                : value,
                    }),
                    false,
                    'chatUI/setLastExportFilePath',
                ),

            setLastExportDirectory: (value) =>
                set(
                    (state) => ({
                        lastExportDirectory:
                            typeof value === 'function'
                                ? value(state.lastExportDirectory)
                                : value,
                    }),
                    false,
                    'chatUI/setLastExportDirectory',
                ),
        }),
        { enabled: enableDevtools, name: 'chat-ui-store' },
    ),
);
